import reqwest from 'reqwest';
import { toQuery, nodeWithArgs, toMutation } from 'common/util/queryHelper';
import { queryGraphQL } from 'common/util/query';
import FileUploadQueue from 'common/util/FileUploadQueue';
import {addApplicationMessage} from 'common/actions/applicationMessagesActions';

/**
 * FileUploadProcessor
 *
 * Used to manage the file upload processes in worker threads, if supported
 */

export const UploadStatus = {
    loaded: 'loaded',
    uploading: 'uploading',
    uploaded: 'uploaded',
    error: 'error',
    canceled: 'canceled'
};

export const UploadEvents = {
    start: 'start',
    startBatch: 'startBatch',
    progress: 'progress',
    complete: 'complete',
    completeBatch: 'completeBatch',
    success: 'success',
    error: 'error',
    cancel: 'cancel'
};

import EventEmitter from 'events';

class FileUploadProcessor extends EventEmitter {

    constructor(options = {}, store) {
        super();
        this.fileId2xhr = {};
        this.uploadingFiles = [];
        this.maxConcurrentUploads = options.maxConcurrentUploads || 5;
        this.cors = options.cors;
        this.fileUploadQueue = new FileUploadQueue();
        this.store = store;
        this.dispatch = store.dispatch;

    }

    abort(fileId) {
        const xhr = this.fileId2xhr[fileId];
        if (xhr) {
            xhr.abort();
            return true;
        }
        return false;
    }

    /*
     * Retrieves Upload Requests for each file, then uploads the files to storage,
     * and finishes with sending a createDocument request to Chili to track
     * the newly uploaded document in mongo.
     */
    processFiles(files, binderInfo) {
        files = files.filter(file => file.status === UploadStatus.loaded);
        var getTempUrls = this.getUploadRequests(files.length, binderInfo);
        this.emit('startBatch', files);
        var fileProcesses = getTempUrls.then(uploadRequests => {
            if (!uploadRequests || uploadRequests.length != files.length) {
                this.dispatch(addApplicationMessage('error', 'Unable to process file upload at this time.'));
            }
            files.forEach((file, index) => {
                var uploadRequest = uploadRequests[index];
                this.fileUploadQueue.enqueue({
                    file: file,
                    binderInfo: binderInfo,
                    uploadRequest: uploadRequest
                });
            });
        });
        return fileProcesses.then(() => this.uploadNextFile())
            .then(filesProcessed => {
                this.emit(UploadEvents.completeBatch, filesProcessed);
                return filesProcessed;
            });
    }

    uploadNextFile() {
        while (!this.fileUploadQueue.isEmpty() && this.uploadingFiles.length < this.maxConcurrentUploads) {
            let fileUpload = this.fileUploadQueue.dequeue();
            const {file, binderInfo, uploadRequest} = fileUpload;
            let processFilePromise = this.processFile(file, binderInfo, uploadRequest).then(() => {
                var uploadingFileIndex = this.uploadingFiles.indexOf(processFilePromise);
                if (uploadingFileIndex > -1) {
                    this.uploadingFiles.splice(uploadingFileIndex, 1);
                }
            });
            this.uploadingFiles.push(processFilePromise);
        }
        return Promise.race(this.uploadingFiles).then(() => {
            if (this.fileUploadQueue.isEmpty()) {
                return Promise.resolve(null);
            }
            return this.uploadNextFile();
        });
    }

    processFile(file, binderInfo, uploadRequest) {
        file.status = UploadStatus.uploading;
        this.emit(UploadEvents.start, file);
        var getUploadRequest = uploadRequest
            ? Promise.resolve(uploadRequest)
            : this.getUploadRequests(1, binderInfo).then(uploadRequests => uploadRequests[0]);
        return getUploadRequest.then(uploadRequest => this.uploadFileToStorage(file, uploadRequest).then(file => {
            return this.createDocumentForFile(file, binderInfo, uploadRequest);
        }).then(() => {
            file.status = UploadStatus.uploaded;
            this.emit(UploadEvents.success, file);
            return file;
        })).catch(err => {
            file.error = err;
            file.status = UploadStatus.error;
            this.emit(UploadEvents.error, file);
            return file;
        }).then(file => {
            file.progress = 1;
            file.progressUnknown = false;
            this.emit(UploadEvents.complete, file);
            return file;
        });
    }

    getUploadRequests(fileCount, binderInfo) {
        if (fileCount === 0) {
            return Promise.resolve([]);
        }
        const query = toQuery(nodeWithArgs('uploadRequests', {
            count: fileCount,
            binderId: binderInfo.binderId,
            institutionId: binderInfo.institutionId
        }, [
            'tempUrl',
            'objectName',
            'containerId'
        ]));

        return queryGraphQL(query).then(results => {
            if (!results.errors) {
                return results.data.uploadRequests;
            }
        });
    }

    uploadFileToStorage(file, uploadRequest) {
        const request = reqwest({
            url: uploadRequest.tempUrl,
            method: 'put',
            crossOrigin: true,
            processData: false,  //must be false for formData
            contentType: file.file.type,
            headers: {
                'x-ms-blob-type': 'BlockBlob'
            },
            data: file.result,
            xhr: o => {
                var xhr = new XMLHttpRequest();
                this.fileId2xhr[file.id] = xhr;
                xhr.upload.addEventListener('progress', e => {
                    if (e.lengthComputable) {
                        file.progress = e.loaded / e.total;
                        file.progressUnknown = false;
                    } else {
                        file.progress = 0;
                        file.progressUnknown = true;
                    }
                    this.emit(UploadEvents.progress, file);
                });
                return xhr;
            }
        });
        return request.then(response => {
            delete this.fileId2xhr[file.id];
            return file;
        });
    }

    createDocumentForFile(file, binderInfo, uploadRequest) {
        const document = {
            name: file.fileName,
            binderId: binderInfo.binderId,
            objectName: uploadRequest.objectName,
            containerId: uploadRequest.containerId,
            fileName: file.fileName,
            fileSize: file.file.size,
            mimeType: file.file.type
        };
        let query = toMutation(nodeWithArgs('addDocument', {
            document: document
        }));

        return queryGraphQL(query).then(results => {
            if (results.errors) {
                this.dispatch(addApplicationMessage('error', 'failed to create document for uploaded file'));
                throw new Error('failed to create document for uploaded file', JSON.stringify(results.errors));
            }
            else {
                return Boolean(results.data.addDocument);
            }
        });
    }
}

export default FileUploadProcessor;
